import { pb } from '@/lib/pocketbase';
import { supabase } from '@/integrations/supabase/client';

/**
 * Ntfy notification service
 * Sends push notifications via ntfy.sh to users who have configured a topic
 */
export const ntfyService = {
  /**
   * Get user's ntfy topic from PocketBase
   */
  getUserNtfyTopic: async (userId: string): Promise<string | null> => {
    try {
      const user = await pb.collection('users').getOne(userId);
      return user.ntfyTopic || null;
    } catch (error) {
      console.error('❌ [NTFY] Error getting user ntfy topic:', error);
      return null;
    }
  },

  /**
   * Save user's ntfy topic to PocketBase
   */
  saveUserNtfyTopic: async (userId: string, topic: string): Promise<boolean> => {
    try {
      await pb.collection('users').update(userId, { ntfyTopic: topic });
      console.log('✅ [NTFY] Saved ntfy topic for user:', userId);
      return true;
    } catch (error) {
      console.error('❌ [NTFY] Error saving ntfy topic:', error);
      return false;
    }
  },

  /**
   * Send notification to a user via ntfy (if they have a topic configured)
   */
  sendNotification: async (
    userId: string,
    title: string,
    message: string,
    options?: {
      priority?: number;
      tags?: string[];
      click?: string;
    }
  ): Promise<boolean> => {
    try {
      // Get user's ntfy topic
      const topic = await ntfyService.getUserNtfyTopic(userId);
      
      if (!topic) {
        console.log('⚠️ [NTFY] User has no ntfy topic configured:', userId);
        return false;
      }

      console.log('📣 [NTFY] Sending notification to user:', userId, 'topic:', topic);

      // Send via edge function
      const { data, error } = await supabase.functions.invoke('send-ntfy-notification', {
        body: {
          topic,
          title,
          message,
          priority: options?.priority || 3,
          tags: options?.tags || [],
          click: options?.click
        }
      });

      if (error) {
        console.error('❌ [NTFY] Edge function error:', error);
        return false;
      }

      console.log('✅ [NTFY] Notification sent successfully:', data);
      return true;
    } catch (error) {
      console.error('❌ [NTFY] Error sending notification:', error);
      return false;
    }
  },

  /**
   * Get base URL for notifications - uses custom domain in production
   */
  getBaseUrl: (): string => {
    const customDomain = 'https://tutornesta.com';
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
      return customDomain;
    }
    return typeof window !== 'undefined' ? window.location.origin : customDomain;
  },

  /**
   * Send message notification via ntfy
   */
  sendMessageNotification: async (receiverId: string, senderName: string): Promise<boolean> => {
    const baseUrl = ntfyService.getBaseUrl();
    return ntfyService.sendNotification(
      receiverId,
      '💬 New Message',
      `You have a new message from ${senderName}`,
      {
        priority: 4,
        tags: ['speech_balloon'],
        click: `${baseUrl}/messages`
      }
    );
  },

  /**
   * Send booking confirmation notification via ntfy
   */
  sendBookingNotification: async (
    userId: string,
    date: string,
    otherPartyName: string,
    isForStudent: boolean
  ): Promise<boolean> => {
    const baseUrl = ntfyService.getBaseUrl();
    const message = isForStudent
      ? `Your session with ${otherPartyName} on ${date} has been confirmed.`
      : `You have a new booking with ${otherPartyName} on ${date}.`;

    return ntfyService.sendNotification(
      userId,
      '📅 Booking Confirmed',
      message,
      {
        priority: 4,
        tags: ['calendar', 'white_check_mark'],
        click: `${baseUrl}/schedule`
      }
    );
  },

  /**
   * Test ntfy notification (direct to topic without user lookup)
   */
  sendTestNotification: async (topic: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('send-ntfy-notification', {
        body: {
          topic,
          title: '🎉 TutorNesta Test',
          message: 'If you see this, ntfy notifications are working!',
          priority: 3,
          tags: ['white_check_mark', 'bell']
        }
      });

      if (error) {
        console.error('❌ [NTFY] Test notification error:', error);
        return false;
      }

      console.log('✅ [NTFY] Test notification sent:', data);
      return true;
    } catch (error) {
      console.error('❌ [NTFY] Test notification failed:', error);
      return false;
    }
  }
};
